/**
 * Utility functions for country detection
 */

/**
 * Get country code from browser locale
 * @returns {string} - Two-letter country code (e.g., 'US', 'GB')
 */
export const getCountryFromLocale = () => {
  try {
    // Try to get country from browser locale
    const locale = navigator.language || navigator.userLanguage;
    
    // Locale format is usually 'en-US', 'en-GB', etc.
    if (locale && locale.includes('-')) {
      const countryCode = locale.split('-')[1];
      return countryCode.toUpperCase();
    }
    
    // If no region info, try to map language to most common country
    const languageToCountry = {
      'en': 'US',
      'es': 'ES',
      'fr': 'FR',
      'de': 'DE',
      'it': 'IT',
      'pt': 'BR',
      'ja': 'JP',
      'ko': 'KR',
      'zh': 'CN',
      'ar': 'SA',
      'ru': 'RU'
    };
    
    const language = locale.split('-')[0].toLowerCase();
    return languageToCountry[language] || 'US';
  } catch (error) {
    console.error('Error detecting country from locale:', error);
    return 'US'; // Default fallback
  }
};

/**
 * Get country code from IP address using ipapi.co (free, no API key required)
 * @returns {Promise<string>} - Two-letter country code
 */
export const getCountryFromIP = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.country_code || 'US';
    }
    
    return 'US';
  } catch (error) {
    console.error('Error detecting country from IP:', error);
    return 'US';
  }
};

/**
 * Auto-detect user's country code
 * Tries IP-based detection first, falls back to locale
 * @returns {Promise<string>} - Two-letter country code
 */
export const autoDetectCountry = async () => {
  try {
    // Try IP-based detection (more accurate)
    const countryFromIP = await Promise.race([
      getCountryFromIP(),
      new Promise((resolve) => setTimeout(() => resolve(null), 2000)) // 2s timeout
    ]);
    
    if (countryFromIP) {
      console.log('Country detected from IP:', countryFromIP);
      return countryFromIP;
    }
    
    // Fallback to locale-based detection
    const countryFromLocale = getCountryFromLocale();
    console.log('Country detected from locale:', countryFromLocale);
    return countryFromLocale;
  } catch (error) {
    console.error('Error auto-detecting country:', error);
    return 'US'; // Ultimate fallback
  }
};

/**
 * Get timezone-based country guess
 * @returns {string} - Two-letter country code
 */
export const getCountryFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Map common timezones to countries
    const timezoneToCountry = {
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Toronto': 'CA',
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Madrid': 'ES',
      'Europe/Rome': 'IT',
      'Asia/Tokyo': 'JP',
      'Asia/Seoul': 'KR',
      'Asia/Shanghai': 'CN',
      'Asia/Hong_Kong': 'HK',
      'Asia/Singapore': 'SG',
      'Australia/Sydney': 'AU',
      'Pacific/Auckland': 'NZ'
    };
    
    return timezoneToCountry[timezone] || 'US';
  } catch (error) {
    console.error('Error detecting country from timezone:', error);
    return 'US';
  }
};

/**
 * Smart country detection with multiple fallbacks
 * @returns {Promise<string>} - Two-letter country code
 */
export const detectCountry = async () => {
  // Try IP-based detection; default to US (+1) on any error or timeout
  try {
    const ipCountry = await Promise.race([
      getCountryFromIP(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 1500))
    ]);
    if (ipCountry) return ipCountry;
  } catch (error) {
    console.warn('IP country detection failed, defaulting to US (+1):', error.message);
    return 'US'; // Default to +1 on error
  }

  // Final fallback
  return 'US';
};
